// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Federal bills browser API service.
 *
 * Bills and summaries are served via the connector cache data endpoint.
 * PDF URLs use the legislative-review application route.
 */
import type { NodeApiClient } from '@/app-shell/services'
import type { FederalBillMetadata } from '../models/types'

/** Response from the connector data endpoint */
interface ConnectorDataResponse {
  downloadUrl?: string
  content?: string
  contentType: string
}

/** Combined result from API metadata + S3 download */
export interface FederalBillsFetchResult {
  bills: FederalBillMetadata[]
  cacheStatus: string | undefined
}

/** Convert snake_case keys to camelCase (shallow, one level). */
function snakeToCamel(key: string): string {
  return key.replace(/_([a-z])/g, (_, c) => c.toUpperCase())
}

/** Recursively convert all snake_case keys in an object to camelCase. */
function camelizeKeys(obj: any): any {
  if (Array.isArray(obj)) return obj.map(camelizeKeys)
  if (obj !== null && typeof obj === 'object') {
    const result: Record<string, any> = {}
    for (const [key, value] of Object.entries(obj)) {
      result[snakeToCamel(key)] = camelizeKeys(value)
    }
    return result
  }
  return obj
}

/**
 * Fetch federal bills for a congress via connector cache.
 */
export async function fetchFederalBills(
  apiClient: NodeApiClient,
  _orgId: string,
  _wsId: string,
  congress: number,
): Promise<FederalBillsFetchResult> {
  const params = new URLSearchParams({ document_type: 'bulk_bills' })
  const url = `/connectors/federal-bills/caches/${congress}/data?${params}`
  const res = await apiClient.get<ConnectorDataResponse>(url)

  if (!res.data.downloadUrl) {
    return { bills: [], cacheStatus: undefined }
  }

  const s3Res = await fetch(res.data.downloadUrl)
  if (!s3Res.ok) {
    throw new Error(`Failed to download bills cache: ${s3Res.status}`)
  }
  const raw = await s3Res.json()
  // Each entry is {"updated_at": "...", "bill": {...}} — unwrap the bill
  const bills: FederalBillMetadata[] = (raw.bills ?? []).map(
     
    (entry: any) => camelizeKeys(entry.bill ?? entry),
  )

  return { bills, cacheStatus: 'ready' }
}

/** Raw summary from the backend (snake_case keys). */
export interface BillSummaryEntry {
  versionCode: string
  actionDate?: string | null
  actionDesc?: string | null
  updateDate?: string | null
  text: string
  policyArea?: string | null
  legislativeSubjects?: string[] | null
}

/** Map of "billType-billNumber" -> list of summary versions */
export type BillSummariesMap = Map<string, BillSummaryEntry[]>

/**
 * Fetch all bill summaries for a congress via connector cache.
 */
export async function fetchFederalBillSummaries(
  apiClient: NodeApiClient,
  _orgId: string,
  _wsId: string,
  congress: number,
): Promise<BillSummariesMap> {
  const params = new URLSearchParams({ document_type: 'bulk_summaries' })
  const url = `/connectors/federal-bills/caches/${congress}/data?${params}`
  const res = await apiClient.get<ConnectorDataResponse>(url)

  if (!res.data.downloadUrl) {
    return new Map()
  }

  const s3Res = await fetch(res.data.downloadUrl)
  if (!s3Res.ok) {
    throw new Error(`Failed to download summaries: ${s3Res.status}`)
  }

   
  const raw: Record<string, any> = await s3Res.json()
  const result: BillSummariesMap = new Map()
  for (const [key, value] of Object.entries(raw)) {
    // Support both new format {"updated_at": "...", "summaries": [...]} and legacy [...]
    const entries = Array.isArray(value) ? value : (value?.summaries ?? [])
    result.set(key, (entries as unknown[]).map(camelizeKeys) as BillSummaryEntry[])
  }
  return result
}
